import { useState, useEffect } from "react";
import api from "api";
import 'styles/quotemobiledetail.css';

// ligne d'un équipement : 2 lignes (libellé puis icônes), + ligne de note dépliable entre les deux
const QuoteMobileItemRow = ({ item, onStatusChange, onNoteChange }) => {

    const [ noteOpen, setNoteOpen ] = useState(false);
    const [ noteDraft, setNoteDraft ] = useState(item.note || "");

    const saveNote = () => {
        if (noteDraft !== item.note) onNoteChange(item, noteDraft);
    };

    return (
        <div className={`QuoteMobileDetailItem QuoteMobileDetailItem-${item.status || 'none'}`}>
            <div className="QuoteMobileDetailItemLine1">
                {item.quantity}× {item.name}
            </div>
            {
                (noteOpen || item.note)
                &&
                <div className="QuoteMobileDetailItemNoteRow">
                    <input
                        type="text"
                        value={noteDraft}
                        placeholder="Commentaire..."
                        onChange={(e) => setNoteDraft(e.target.value)}
                        onBlur={saveNote}
                    />
                </div>
            }
            <div className="QuoteMobileDetailItemLine2">
                <button
                    type="button"
                    className={`QuoteMobileDetailStatusButton${item.status === 'ready' ? " QuoteMobileDetailStatusButtonActive" : ""}`}
                    title="Prêt"
                    onClick={() => onStatusChange(item, 'ready')}
                >
                    ✅
                </button>
                <button
                    type="button"
                    className={`QuoteMobileDetailStatusButton${item.status === 'later' ? " QuoteMobileDetailStatusButtonActive" : ""}`}
                    title="À rajouter"
                    onClick={() => onStatusChange(item, 'later')}
                >
                    🕓
                </button>
                <button
                    type="button"
                    className={`QuoteMobileDetailStatusButton QuoteMobileDetailNoteButton${item.note ? " QuoteMobileDetailNoteButtonActive" : ""}`}
                    title="Note"
                    onClick={() => setNoteOpen(prev => !prev)}
                >
                    📝
                </button>
            </div>
        </div>
    );

};

// écran mobile dédié à un devis : liste épurée (quantité + nom), catégories repliables, statut de préparation et note par article
const QuoteMobileDetail = ({ quoteid, onBack }) => {

    const [ items, setItems ] = useState([]);
    const [ quote, setQuote ] = useState(null);
    const [ staff, setStaff ] = useState([]);
    const [ delivery, setDelivery ] = useState([]);
    const [ infoOpen, setInfoOpen ] = useState(false); // dépliage contact / personnel / transport
    const [ onlyNotReady, setOnlyNotReady ] = useState(false); // filtre "non prêts"
    const [ collapsedCategories, setCollapsedCategories ] = useState({}); // sections repliées

    useEffect(() => {
        api.get(`quote/${quoteid}/items/mobile`)
        .then(response => setItems(response.data));
        api.get(`quote/${quoteid}`)
        .then(response => setQuote(response.data));
        api.get(`staff/${quoteid}/get`)
        .then(response => setStaff(response.data));
        api.get(`delivery/${quoteid}/get`)
        .then(response => setDelivery(response.data));
    }, [quoteid]);

    // re-taper sur le statut déjà actif l'annule (retour à "à traiter")
    const handleStatusChange = (item, status) => {
        const newStatus = item.status === status ? '' : status;
        api.put(`item/${item.id}/status`, { status: newStatus });
        setItems(prev => prev.map(i => i.id === item.id ? { ...i, status: newStatus } : i));
    };

    const handleNoteChange = (item, note) => {
        api.put(`item/${item.id}/note`, { note });
        setItems(prev => prev.map(i => i.id === item.id ? { ...i, note } : i));
    };

    const toggleCategory = (categoryId) => {
        setCollapsedCategories(prev => ({ ...prev, [categoryId]: !prev[categoryId] }));
    };

    const visibleItems = onlyNotReady ? items.filter(i => i.status !== 'ready') : items;

    const categories = [];
    const categoryIndex = {};
    visibleItems.forEach(item => {
        const key = item.category_id;
        if (!(key in categoryIndex)) {
            categoryIndex[key] = { id: key, name: item.category_name, items: [] };
            categories.push(categoryIndex[key]);
        }
        categoryIndex[key].items.push(item);
    });

    const title = quote ? (quote.event || quote.name) : "";

    return (
        <div className="QuoteMobileDetail">
            <div className="QuoteMobileDetailHeader">
                <button className="QuoteMobileDetailBack" onClick={onBack}>←</button>
                <div className="QuoteMobileDetailTitle" onClick={() => setInfoOpen(prev => !prev)}>
                    {title} <span className="QuoteMobileDetailChevron">{infoOpen ? "▲" : "▼"}</span>
                </div>
                <button
                    type="button"
                    className={`QuoteMobileDetailFilterButton${onlyNotReady ? " QuoteMobileDetailFilterButtonActive" : ""}`}
                    onClick={() => setOnlyNotReady(prev => !prev)}
                    title="N'afficher que les articles non prêts"
                >
                    Non prêts
                </button>
            </div>
            {
                infoOpen
                &&
                quote
                &&
                <div className="QuoteMobileDetailInfoPanel">
                    <div>
                        <strong>Contact :</strong> {quote.name}
                        {quote.phone && ` - ${quote.phone}`}
                        {quote.email && ` - ${quote.email}`}
                    </div>
                    <div>
                        <strong>Personnel :</strong> {staff.length ? staff.map(s => s.desc).join(', ') : "aucun"}
                    </div>
                    <div>
                        <strong>Transport :</strong> {delivery.length ? delivery.map(d => d.desc).join(', ') : "aucun"}
                    </div>
                </div>
            }
            <div className="QuoteMobileDetailBody">
                {
                    categories.map(category => (
                        <div key={category.id} className="QuoteMobileDetailCategory">
                            <div
                                className="QuoteMobileDetailCategoryTitle"
                                onClick={() => toggleCategory(category.id)}
                            >
                                <span className="QuoteMobileDetailChevron">
                                    {collapsedCategories[category.id] ? "▶" : "▼"}
                                </span>
                                {category.name}
                            </div>
                            {
                                !collapsedCategories[category.id]
                                &&
                                category.items.map(item => (
                                    <QuoteMobileItemRow
                                        key={item.id}
                                        item={item}
                                        onStatusChange={handleStatusChange}
                                        onNoteChange={handleNoteChange}
                                    />
                                ))
                            }
                        </div>
                    ))
                }
            </div>
        </div>
    );

};

export default QuoteMobileDetail;
